import { useState, useMemo } from "react";
import { DRIVERS, CONFIDENCE_LEVELS, INITIATIVE_STAGES, PRIMARY_SURFACES, buildSprints, S, VALUE_BADGE, scoreStyle, scoreLabel } from "./constants";

const EXEC_BUCKETS = [
  {
    id: "compliance", label: "Compliance Hold", icon: "⚖️",
    color: "#BA1C1C", bg: "#FEF1F1", border: "#FAC8C8",
    desc: "Needs legal/compliance sign-off or App Store risk before build commitment",
    match: p => p.legalRisk || p.appStoreRisk,
  },
  {
    id: "blockers", label: "External Blocker", icon: "🔗",
    color: "#C45612", bg: "#FFF4EA", border: "#FAC890",
    desc: "Waiting on a partner or vendor to deliver before progress can continue",
    match: p => !!p.externalDependency?.trim(),
  },
  {
    id: "partners", label: "Partner-Gated", icon: "🤝",
    color: "#B87010", bg: "#FFF7E0", border: "#F0D490",
    desc: "Requires signed partner agreements before the build makes sense",
    match: p => !!p.partnerDependencies?.trim(),
  },
  {
    id: "atrisk", label: "At Risk / Low Confidence", icon: "🚩",
    color: "#8A5E10", bg: "#FFF4DC", border: "#E8C870",
    desc: "Flagged at-risk or delivery confidence is low",
    match: p => p.atRisk || p.confidenceLevel === "low",
  },
  {
    id: "ontrack", label: "On Track", icon: "✓",
    color: "#0A7840", bg: "#E4F8ED", border: "#80D8A8",
    desc: "No blocking flags — proceeding as planned",
    match: () => true,
  },
];

function ConfidenceBadge({ level }) {
  if (!level) return null;
  const c = CONFIDENCE_LEVELS[level];
  if (!c) return null;
  return (
    <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:4,
      background:c.bg, color:c.color, border:`1px solid ${c.border}`, whiteSpace:"nowrap" }}>
      {c.label}
    </span>
  );
}

function StageBadge({ stage }) {
  if (!stage) return null;
  const s = INITIATIVE_STAGES[stage];
  if (!s) return null;
  return (
    <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:4,
      background:s.bg, color:s.color, border:`1px solid ${s.border}`, whiteSpace:"nowrap" }}>
      {s.icon} {s.label}
    </span>
  );
}

function ModeToggle({ mode, onChange }) {
  return (
    <div style={{ display:"flex", background:"var(--bg-subtle)", borderRadius:6, padding:2, gap:2, marginLeft:8 }}>
      {[
        { id:"release",  label:"Release Date" },
        { id:"complete", label:"Code Complete" },
        { id:"exec",     label:"Exec View" },
      ].map((m) => (
        <button key={m.id} onClick={() => onChange(m.id)} style={{
          padding:"4px 10px", borderRadius:4, border:"none", cursor:"pointer",
          fontSize:11, fontWeight:700, fontFamily:"inherit",
          background: mode===m.id ? (m.id === "exec" ? "#1E3A5F" : "var(--bg-card)") : "transparent",
          color:      mode===m.id ? (m.id === "exec" ? "#93C5FD"  : "var(--text-primary)") : "var(--text-secondary)",
          boxShadow:  mode===m.id ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
          transition:"all 0.15s",
        }}>{m.label}</button>
      ))}
    </div>
  );
}

export default function DigestView({ projects, completed, lanes = [], sprintYear, scoringMethod = "score", onEdit }) {
  const [filterLane, setFilterLane]     = useState("all");
  const [filterDriver, setFilterDriver] = useState("all");
  const [filterStage, setFilterStage]   = useState("all");
  const [digestMode, setDigestMode]     = useState("release"); // "release" | "complete" | "exec"

  const SPRINTS = useMemo(() => buildSprints(sprintYear || new Date().getFullYear()), [sprintYear]);

  const allItems = useMemo(() => [
    ...projects.map((p) => ({ ...p })),
    ...completed.map((p) => ({ ...p, isCompleted: true })),
  ], [projects, completed]);

  const filtered = allItems
    .filter((p) => filterLane   === "all" || p.lane   === filterLane)
    .filter((p) => filterDriver === "all" || p.driver === filterDriver)
    .filter((p) => filterStage  === "all" || p.initiativeStage === filterStage);

  const hasActiveFilters = filterLane !== "all" || filterDriver !== "all" || filterStage !== "all";
  const clearFilters = () => { setFilterLane("all"); setFilterDriver("all"); setFilterStage("all"); };

  const filterBarJsx = (
    <div style={{ display:"flex", gap:8, marginBottom:24, alignItems:"center", flexWrap:"wrap" }}>
      <span style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em", marginRight:4 }}>Filter</span>
      <select value={filterLane} onChange={(e) => setFilterLane(e.target.value)} style={S.filterSelect} aria-label="Filter by team">
        <option value="all">All Teams</option>
        {lanes.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
      </select>
      <select value={filterDriver} onChange={(e) => setFilterDriver(e.target.value)} style={S.filterSelect} aria-label="Filter by driver">
        <option value="all">All Drivers</option>
        {Object.entries(DRIVERS).map(([k,d]) => <option key={k} value={k}>{d.label}</option>)}
      </select>
      <select value={filterStage} onChange={(e) => setFilterStage(e.target.value)} style={S.filterSelect} aria-label="Filter by initiative stage">
        <option value="all">All Stages</option>
        {Object.entries(INITIATIVE_STAGES).map(([k,s]) => <option key={k} value={k}>{s.icon} {s.label}</option>)}
      </select>
      {hasActiveFilters && (
        <button onClick={clearFilters} style={{ ...S.btnClearFilter, whiteSpace:"nowrap", flexShrink:0 }}>✕ Clear</button>
      )}
      <ModeToggle mode={digestMode} onChange={setDigestMode} />
    </div>
  );

  // ── Exec View ─────────────────────────────────────────────────────────────────
  if (digestMode === "exec") {
    const seen = new Set();
    const bucketed = EXEC_BUCKETS.map(bucket => {
      const items = filtered.filter(p => !seen.has(p.id) && bucket.match(p));
      items.forEach(p => seen.add(p.id));
      return { ...bucket, items };
    });

    return (
      <div style={{ flex:1, overflowY:"auto", padding:"24px 32px" }}>
        {filterBarJsx}
        <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
          {bucketed.filter(b => b.id !== "ontrack" || b.items.length > 0).map(bucket => (
            <div key={bucket.id}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                <span style={{ fontSize:14 }}>{bucket.icon}</span>
                <span style={{ fontSize:13, fontWeight:800, color:bucket.color, letterSpacing:"-0.01em" }}>{bucket.label}</span>
                <span style={{ fontSize:10, fontWeight:700, padding:"1px 8px", borderRadius:10,
                  background:bucket.bg, color:bucket.color, border:`1px solid ${bucket.border}` }}>
                  {bucket.items.length}
                </span>
                <span style={{ fontSize:11, color:"var(--text-muted)", fontStyle:"italic" }}>{bucket.desc}</span>
              </div>

              {bucket.items.length === 0 ? (
                <div style={{ fontSize:12, color:"var(--text-muted)", padding:"12px 16px",
                  background:"var(--bg-subtle)", borderRadius:8, border:"1px solid var(--border)",
                  fontStyle:"italic" }}>None — clear ✓</div>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:10 }}>
                  {bucket.items.map(p => {
                    const lane = lanes.find(l => l.id === p.lane);
                    const drv  = DRIVERS[p.driver] || DRIVERS.core;
                    return (
                      <div key={p.id}
                        onClick={onEdit ? () => onEdit(p) : undefined}
                        style={{
                          background:"var(--bg-card)", borderRadius:9, overflow:"hidden",
                          border:`1px solid ${bucket.border}`,
                          borderLeft:`4px solid ${bucket.color}`,
                          boxShadow:"0 1px 3px rgba(0,0,0,0.06)",
                          padding:"12px 14px",
                          display:"flex", flexDirection:"column", gap:8,
                          cursor: onEdit ? "pointer" : "default",
                          transition:"box-shadow 0.12s",
                        }}
                        onMouseEnter={(e) => { if (onEdit) e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)"; }}
                        onMouseLeave={(e) => { if (onEdit) e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; }}
                      >
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:6 }}>
                          <span style={{ fontSize:12, fontWeight:700, color:"var(--text-primary)", lineHeight:1.4, flex:1 }}>
                            {p.legalRisk    && <span title="Legal risk" style={{ marginRight:3 }}>⚖️</span>}
                            {p.appStoreRisk && <span title="App Store risk" style={{ marginRight:3 }}>📱</span>}
                            {p.atRisk       && <span title="At risk" style={{ marginRight:3 }}>🚩</span>}
                            {p.aiInitiative && <span title="AI initiative" style={{ marginRight:3 }}>🤖</span>}
                            {p.name}
                          </span>
                          <div style={{ display:"flex", flexDirection:"column", gap:3, alignItems:"flex-end", flexShrink:0 }}>
                            <StageBadge stage={p.initiativeStage} />
                            <ConfidenceBadge level={p.confidenceLevel} />
                          </div>
                        </div>

                        {p.targetLaunchDate && (
                          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                            <span style={{ fontSize:10, color:"var(--text-muted)", fontWeight:700 }}>Target:</span>
                            <span style={{ fontSize:11, fontWeight:700, color:"var(--text-primary)" }}>
                              {new Date(p.targetLaunchDate + "T00:00:00").toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" })}
                            </span>
                          </div>
                        )}

                        {p.externalDependency?.trim() && (
                          <div style={{ fontSize:11, color:"#C45612", background:"#FFF4EA", border:"1px solid #FAC890",
                            borderRadius:5, padding:"5px 8px", lineHeight:1.4 }}>
                            <span style={{ fontWeight:700 }}>Blocker: </span>{p.externalDependency}
                          </div>
                        )}

                        {p.partnerDependencies?.trim() && (
                          <div style={{ fontSize:11, color:"#B87010", background:"#FFF7E0", border:"1px solid #F0D490",
                            borderRadius:5, padding:"5px 8px", lineHeight:1.4 }}>
                            <span style={{ fontWeight:700 }}>Partners: </span>{p.partnerDependencies}
                          </div>
                        )}

                        {p.successMetrics?.trim() && (
                          <div style={{ fontSize:10, color:"var(--text-secondary)", lineHeight:1.4 }}>
                            <span style={{ fontWeight:700, color:"var(--text-muted)" }}>KPIs: </span>{p.successMetrics}
                          </div>
                        )}

                        <div style={{ display:"flex", gap:4, flexWrap:"wrap", alignItems:"center", paddingTop:4, borderTop:"1px solid var(--border)" }}>
                          {lane && <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4,
                            background:`${lane.color}18`, color:lane.color, border:`1px solid ${lane.color}40` }}>{lane.label}</span>}
                          <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4,
                            background:drv.bg, color:drv.color, border:`1px solid ${drv.border}` }}>{drv.short}</span>
                          {p.primarySurface && PRIMARY_SURFACES[p.primarySurface] && (() => {
                            const sf = PRIMARY_SURFACES[p.primarySurface];
                            return <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4,
                              background:sf.bg, color:sf.color, border:`1px solid ${sf.border}` }}>{sf.label}</span>;
                          })()}
                          {p.value && <span style={{ fontSize:9, color:"#15803D", fontWeight:600 }}>{p.value}</span>}
                          {p.isCompleted && <span style={{ fontSize:9, color:"#0369A1", fontWeight:700 }}>✓ Done</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Release / Code Complete views ─────────────────────────────────────────────
  const scheduledItems = filtered.filter(p => p.sprint !== undefined);

  const monthMap = {};
  scheduledItems.forEach((p) => {
    const lastSprint = SPRINTS[Math.min(p.sprint + p.sprints - 1, SPRINTS.length - 1)];
    if (!lastSprint) return;
    let baseDate = new Date(lastSprint.end);
    if (digestMode === "release") {
      if (baseDate.getDate() > 24) {
        baseDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 1);
      }
    }
    const monthKey   = baseDate.toLocaleString("default", { month: "long", year: "numeric" });
    const monthOrder = baseDate.getFullYear() * 100 + baseDate.getMonth();
    if (!monthMap[monthKey]) monthMap[monthKey] = { label: monthKey, order: monthOrder, items: [] };
    monthMap[monthKey].items.push(p);
  });

  const now = new Date();
  for (let i = 0; i < 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const key = d.toLocaleString("default", { month: "long", year: "numeric" });
    const order = d.getFullYear() * 100 + d.getMonth();
    if (!monthMap[key]) monthMap[key] = { label: key, order, items: [] };
  }

  const months = Object.values(monthMap).sort((a, b) => a.order - b.order);

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"24px 32px" }}>
      {filterBarJsx}
      <div style={{ display:"flex", justifyContent:"flex-end", marginTop:-16, marginBottom:16 }}>
        <span style={{ fontSize:11, color:"var(--text-muted)" }}>
          {scheduledItems.length} project{scheduledItems.length !== 1 ? "s" : ""} across {months.length} month{months.length !== 1 ? "s" : ""}
        </span>
      </div>

      {months.length === 0 && (
        <div style={{ textAlign:"center", padding:"60px 0", color:"var(--text-muted)", fontSize:13 }}>No scheduled projects match your filters.</div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))", gap:16 }}>
        {months.map(({ label, items }) => {
          const [monthName, year] = label.split(" ");
          return (
          <div key={label} style={{ background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:10, overflow:"hidden", boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
              <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--border)", background:"var(--bg-subtle)",
                display:"flex", alignItems:"baseline", gap:6 }}>
                <span style={{ fontSize:14, fontWeight:800, color:"var(--text-primary)", letterSpacing:"-0.02em" }}>{monthName}</span>
                <span style={{ fontSize:11, color:"var(--text-muted)", fontWeight:500 }}>{year}</span>
                <span style={{ marginLeft:"auto", fontSize:10, fontWeight:700, background:"var(--bg-hover)",
                  color:"var(--text-secondary)", borderRadius:10, padding:"2px 8px", border:"1px solid var(--border)" }}>{items.length}</span>
              </div>
              <div style={{ padding:"10px 14px", display:"flex", flexDirection:"column", gap:9 }}>
                {items.sort((a,b) => (b.score||0)-(a.score||0)).map((p) => {
                  const lane  = lanes.find((l) => l.id === p.lane);
                  const drv   = DRIVERS[p.driver] || DRIVERS.core;
                  const isNC  = p.status === "notcommitted";
                  const rStyle = scoreStyle(p.score, scoringMethod);
                  return (
                    <div key={p.id}
                      onClick={onEdit ? () => onEdit(p) : undefined}
                      style={{ display:"flex", gap:8, alignItems:"flex-start", borderRadius:5, padding:"3px 4px", margin:"-3px -4px",
                        cursor: onEdit ? "pointer" : "default", transition:"background 0.1s" }}
                      onMouseEnter={(e) => { if (onEdit) e.currentTarget.style.background = "var(--bg-hover)"; }}
                      onMouseLeave={(e) => { if (onEdit) e.currentTarget.style.background = "transparent"; }}
                    >
                      <div style={{ width:3, alignSelf:"stretch", borderRadius:2, background:lane?.color||"#94A3B8",
                        flexShrink:0, opacity: isNC ? 0.4 : 1, marginTop:2 }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:600, lineHeight:1.4, letterSpacing:"-0.01em",
                          color: p.isCompleted ? "#0369A1" : isNC ? "#92400E" : "var(--text-primary)",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {p.aiInitiative && <span style={{ marginRight:3, fontSize:11 }}>🤖</span>}
                          {p.legalRisk    && <span style={{ marginRight:3, fontSize:10 }} title="Legal risk">⚖️</span>}
                          {p.appStoreRisk && <span style={{ marginRight:3, fontSize:10 }} title="App Store risk">📱</span>}
                          {isNC && <span style={{ marginRight:3, fontSize:10, color:"#B45309", fontWeight:800 }}>?</span>}
                          {p.isCompleted && <span style={{ marginRight:3, color:"#0369A1" }}>✓</span>}
                          {VALUE_BADGE(p.value) && <span style={{ marginRight:3 }}>{VALUE_BADGE(p.value)}</span>}
                          {p.name}
                        </div>
                        <div style={{ display:"flex", gap:4, marginTop:4, flexWrap:"wrap", alignItems:"center" }}>
                          <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4,
                            background:lane?.color ? `${lane.color}18` : undefined, color:lane?.color, border:`1px solid ${lane?.color ? `${lane.color}40` : 'var(--border)'}` }}>{lane?.label}</span>
                          <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4,
                            background:drv.bg, color:drv.color, border:`1px solid ${drv.border}` }}>{drv.short}</span>
                          {p.primarySurface && PRIMARY_SURFACES[p.primarySurface] && (() => {
                            const sf = PRIMARY_SURFACES[p.primarySurface];
                            return <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4,
                              background:sf.bg, color:sf.color, border:`1px solid ${sf.border}` }}>{sf.label}</span>;
                          })()}
                          {p.value && <span style={{ fontSize:9, color:"#15803D", fontWeight:600 }}>{p.value}</span>}
                          {p.targetLaunchDate && (
                            <span style={{ fontSize:9, color:"#1A6BC4", fontWeight:700 }} title="Target launch date">
                              🎯 {new Date(p.targetLaunchDate + "T00:00:00").toLocaleDateString("en-US", { month:"short", day:"numeric" })}
                            </span>
                          )}
                          <ConfidenceBadge level={p.confidenceLevel} />
                          <StageBadge stage={p.initiativeStage} />
                        </div>
                      </div>
                      <span style={{ fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:4,
                        background:rStyle.bg, color:rStyle.text, border:`1px solid ${rStyle.border}`,
                        flexShrink:0, alignSelf:"flex-start", lineHeight:1.5 }}>{scoreLabel(p.score, scoringMethod)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ ...S.legend, marginTop:16, borderRadius:8, border:"1px solid var(--border)" }}>
        <span style={S.legendSect}>Team</span>
        {lanes.map((l) => (
          <span key={l.id} style={S.legendItem}><span style={{ ...S.dot, background:l.color }} />{l.label}</span>
        ))}
        <span style={{ ...S.legendSect, marginLeft:16 }}>Driver</span>
        {Object.entries(DRIVERS).map(([k,d]) => (
          <span key={k} style={S.legendItem}><span style={{ ...S.dot, background:d.color }} />{d.label}</span>
        ))}
        <span style={{ ...S.legendSect, marginLeft:16 }}>Score</span>
        <span style={S.legendItem}><span style={{ ...S.dot,background:"#F59E0B" }} />High (4.0+)</span>
        <span style={{ ...S.legendItem,color:"#0369A1",marginLeft:8 }}>✓ Completed</span>
        <span style={{ ...S.legendItem,color:"#B45309",marginLeft:8 }}>? Not Committed</span>
        <span style={{ ...S.legendItem,marginLeft:8 }}>🤖 AI Initiative</span>
        <span style={{ ...S.legendItem,marginLeft:8 }}>⚖️ Legal Risk</span>
        <span style={{ ...S.legendItem,marginLeft:8 }}>📱 App Store Risk</span>
        <span style={{ ...S.legendItem,marginLeft:8 }}>🎯 Target Date</span>
        <span style={{ ...S.legendItem,marginLeft:8 }}>🔥 High Value (500+)</span>
      </div>
    </div>
  );
}
